import React, { useEffect, useState } from "react";
import { reportsApi } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FolderTree, Play } from "lucide-react";
import { toast } from "sonner";

const err = (e) => e?.response?.data?.detail || e.message;

export default function FamiliesPanel() {
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [newFam, setNewFam] = useState("");
  const [move, setMove] = useState({ account_id: "", family_id: "" });

  const load = () => Promise.all([reportsApi.families().then(setData), reportsApi.jobs().then(setJobs)]).catch(console.error);
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-sm text-muted-foreground py-6">Loading account families…</div>;

  const createFam = async () => {
    if (!newFam.trim()) return;
    try {
      await reportsApi.createFamily(newFam);
      toast.success("Account family created");
      setNewFam("");
      load();
    } catch (e) { toast.error("Failed", { description: err(e) }); }
  };

  const moveAcc = async () => {
    if (!move.account_id || !move.family_id) { toast.error("Pick an account and a target family"); return; }
    try {
      await reportsApi.moveAccount(move.account_id, move.family_id);
      toast.success("Account moved — billing data follows at next rating run");
      load();
    } catch (e) { toast.error("Failed", { description: err(e) }); }
  };

  const runJob = async (type, detail) => {
    try {
      const j = await reportsApi.runJob(type, detail);
      toast.success(`Job ${j.id} completed`, { description: `${j.stats.rows.toLocaleString()} rows · ${j.stats.duration_s}s` });
      load();
    } catch (e) { toast.error("Job failed", { description: err(e) }); }
  };

  return (
    <div className="space-y-4" data-testid="families-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.families.map((f) => (
          <div key={f.id} className="cs-card p-4" data-testid={`family-${f.id}`}>
            <div className="flex items-center gap-2">
              <FolderTree className="w-4 h-4 text-[#00579e]" />
              <span className="font-semibold text-sm">{f.name}</span>
              <span className="cs-mono text-[10px] text-muted-foreground ml-auto">payer {f.payer}</span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5 mb-2">{f.description}</div>
            {(f.accounts || []).map((a) => (
              <div key={a.id} className="flex items-center text-xs py-1 border-t border-border/60">
                <span className="cs-mono">{a.id}</span>
                <span className="ml-2 font-medium">{a.name}</span>
                <span className={`ml-auto px-1.5 py-0.5 rounded-sm text-[9px] uppercase font-semibold ${a.type === "payer" ? "bg-[#0B2545] text-white" : "bg-[#e0f2fe] text-[#0369a1]"}`}>{a.type}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="cs-card p-4 flex flex-wrap items-end gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Create family</div>
          <div className="flex gap-2">
            <Input value={newFam} onChange={(e) => setNewFam(e.target.value)} placeholder="Family name"
              data-testid="new-family-input" className="rounded-sm h-9 text-xs w-[200px]" />
            <Button onClick={createFam} data-testid="create-family-button" className="rounded-full h-9 text-xs bg-[#00579e] hover:bg-[#004580] text-white">Create</Button>
          </div>
        </div>
        <div className="md:ml-6">
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Move account to family</div>
          <div className="flex gap-2">
            <Select value={move.account_id} onValueChange={(v) => setMove({ ...move, account_id: v })}>
              <SelectTrigger className="rounded-sm h-9 text-xs w-[180px]" data-testid="move-account-select"><SelectValue placeholder="Account" /></SelectTrigger>
              <SelectContent>{data.accounts.filter((a) => a.type !== "payer").map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={move.family_id} onValueChange={(v) => setMove({ ...move, family_id: v })}>
              <SelectTrigger className="rounded-sm h-9 text-xs w-[180px]" data-testid="move-family-select"><SelectValue placeholder="Target family" /></SelectTrigger>
              <SelectContent>{data.families.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={moveAcc} data-testid="move-account-button" variant="outline" className="rounded-full h-9 text-xs">Move</Button>
          </div>
        </div>
      </div>

      <div className="cs-card p-4">
        <div className="text-xs font-semibold mb-2">Billing-data admin jobs</div>
        <div className="flex flex-wrap gap-2 mb-3">
          {[["copy_billing_family", "Copy billing data → family"], ["copy_billing_payee", "Copy billing data → payee"],
            ["reload_billing", "Reload detailed billing data"]].map(([t, l]) => (
            <Button key={t} size="sm" variant="outline" onClick={() => runJob(t, l)} data-testid={`job-${t}`}
              className="rounded-full h-8 text-xs">
              <Play className="w-3 h-3 mr-1" /> {l}
            </Button>
          ))}
        </div>
        <table className="w-full text-xs">
          <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
            <tr className="text-left">
              {["Job", "Type", "Status", "Started", "Rows", "Duration"].map((h) => (
                <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground" data-testid="no-jobs">No jobs run yet.</td></tr>}
            {jobs.map((j) => (
              <tr key={j.id} className="border-b border-border last:border-0" data-testid={`job-row-${j.id}`}>
                <td className="px-3 py-2 cs-mono">{j.id}</td>
                <td className="px-3 py-2">{j.type}</td>
                <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold bg-[#dcfce7] text-[#15803d]">{j.status}</span></td>
                <td className="px-3 py-2 text-muted-foreground">{(j.started_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2 text-right cs-mono">{j.stats?.rows?.toLocaleString()}</td>
                <td className="px-3 py-2 text-right cs-mono">{j.stats?.duration_s}s</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
